import type { Locale } from '@agapi/mlc-core';
import type { Loop54ClientConfig } from '../client';

import { getClient } from '../client';
import type {
  Loop54CustomDataRequest as CustomData,
  Event,
  EntityCollectionParameters,
  QueryCollectionParameters,
  Entity,
  Response,
  AutoCompleteResponse
} from '../types';

export const search = async <CUSTOM_PARAMS, CUSTOM_DATA>(
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    query: string;
    resultsOptions?: EntityCollectionParameters;
    relatedResultsOptions?: EntityCollectionParameters;
    spellingSuggestionsOptions?: QueryCollectionParameters;
    relatedQueriesOptions?: QueryCollectionParameters;
    customData?: CUSTOM_PARAMS;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response<CUSTOM_DATA>> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'search', body, userId, config });
};

export const autocomplete = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    query: string;
    queriesOptions?: QueryCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<AutoCompleteResponse> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'autoComplete', body, userId, config });
};

export const createEvents = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    events: Event[];
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'createEvents', body, userId, config });
};

export const getEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getEntities', body, userId, config });
};

export const getComplementaryEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    entity: Entity;
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getComplementaryEntities', body, userId, config });
};

export const getBasketRecommendations = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    entities: Entity[];
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path:'getBasketRecommendations', body, userId, config });
};

export const getRecommendedEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getRecommendedEntities', body, userId, config });
};

export const getRelatedEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    entity: Entity;
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getRelatedEntities', body, userId, config });
};

export const getPopularEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    behaviorType: string;
    entityType?: string[];
    forUserId?: string;
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getPopularEntities', body, userId, config });
};

export const getRecentEntities = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    behaviorType: string;
    entityType?: string[];
    forUserId?: string;
    resultsOptions?: EntityCollectionParameters;
    customData?: CustomData;
    userId: string;
    config: Loop54ClientConfig;
  }
): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getRecentEntities', body, userId, config });
};

export const getEntitiesByAttribute = async (
  f: typeof globalThis.fetch,
  locale: Locale,
  params: {
    attribute: { name: string; value: string | number | boolean | Array<string | number | boolean>;
  };
  resultsOptions?: EntityCollectionParameters;
  customData?: CustomData;
  userId: string;
  config: Loop54ClientConfig;
}): Promise<Response> => {
  const { userId, config, ...body } = params;
  return getClient(f, locale, { path: 'getEntitiesByAttribute', body, userId, config });
};
